// Timber & Thread Co. — Final data model

export const SITE = {
  name: 'TIMBER & THREAD CO.',
  tagline: 'Solid timber. Fine fabrics. Made in Australia.',
  email: 'info@timberandthread.com.au',
  phone: '+61 3 9000 0000',
  location: 'Melbourne, Australia',
  hours: 'Mon–Fri, 9am–5pm AEST',
  ig: 'timberandthread.co',
};

export const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Shop', path: '/collections/all' },
  { label: 'Journal', path: '/journal' },
  { label: 'Contact', path: '/contact' },
];

// ============ BRAND ASSETS (local) ============
export const assets = {
  // Videos
  videoHero: '/media/hero-video.mp4',
  video2: '/media/brand-video-2.mp4',
  video3: '/media/brand-video-3.mp4',
  // Optional music — drop /media/hero-music.mp3 to enable
  musicTrack: '/media/hero-music.mp3',

  // Branded
  branded: '/media/branded.jpeg',
  // Branded story image — walnut platform bed with bouclé throw + watermark
  storyBranded: '/media/story-branded-bed.jpeg',

  // Thread (bouclé / fabric)
  threadBed1: '/media/thread-bed-1.jpeg',
  threadBed2: '/media/thread-bed-2.jpeg',
  threadBed3: '/media/thread-bed-3.jpeg',
  threadBed4: '/media/thread-bed-4.jpeg',
  threadBed5: '/media/thread-bed-5.jpeg',
  threadDark: '/media/thread-dark-bedroom.jpeg',
  threadWave: '/media/thread-wave-chaise.jpeg',
  threadCurvedBed: '/media/thread-curved-bed.jpeg',
  threadStyled: '/media/thread-styled-living.jpeg',
  threadBestHome: '/media/thread-best-home.jpeg',
  threadShopInspo: '/media/thread-shop-inspo.jpeg',
  threadStudioApt: '/media/thread-studio-apt.jpeg',
  threadStudio2: '/media/thread-studio-2.jpeg',
  threadNordic: '/media/thread-nordic-living.jpeg',
  threadCurvedSofa: '/media/thread-curved-sofa.jpeg',
  threadFlow: '/media/thread-flow-wave.jpeg',

  // Lifestyle (used in gallery)
  lifeWarm: '/media/life-warm.jpeg',
  lifeSofa: '/media/life-sofa.jpeg',
  lifeOlive: '/media/life-olive.jpeg',
  lifeEvening: '/media/life-evening.jpeg',
  lifeMirror: '/media/life-mirror.jpeg',

  // Timber (mostly solid wood — internal category gate, not user-facing)
  timberHero: '/media/timber-hero.jpeg',
  timberBed: '/media/timber-bed.jpeg',
  timberFloatingPlans: '/media/timber-floating-plans.jpeg',
  timberLimewash: '/media/timber-limewash-platform.jpeg',
  timberMaster: '/media/timber-master-bedroom.jpeg',
  timberEntryway: '/media/timber-entryway-console.jpeg',

  // Light (pendants / chandeliers)
  lightJapandiPendants: '/media/light-japandi-pendants.jpeg',
  lightJapandiDining: '/media/light-japandi-dining.jpeg',
  japandiDining: '/media/japandi-dining.jpeg',

  // Collection tile heroes (user-supplied)
  collLiving:   '/media/coll-living.jpg',
  collDining:   '/media/coll-dining.jpg',
  collMirrors:  '/media/coll-mirrors-v2.jpg',
  collStorage:  '/media/coll-storage-v2.jpg',
  collLighting: '/media/coll-lighting-v2.jpg',

  // Product hero shots (user-supplied, June 23 batch)
  chaiseBoucle: '/media/chaise-boucle.jpeg',
  waveConsoleWalnut: '/media/wave-console-walnut.jpeg',
  curveConsoleWalnut: '/media/console-curved.jpeg',
  floatWalnutBed: '/media/float-walnut-bed.jpeg',
  cloudBoucleBed: '/media/cloud-boucle-bed.jpeg',
  cloudModularSofa: '/media/cloud-modular-sofa.jpeg',
  wasabiPendant1: '/media/pendant-1.jpeg',
  wasabiPendant2: '/media/pendant-2.jpeg',
};

const A = assets;

// Corrected mappings — what each file ACTUALLY shows
const A2 = {
  ...assets,
  // Beds (correctly mapped)
  archBed: assets.lightJapandiPendants, // arched bouclé headboard
  channelBed: assets.threadBed4,         // channel-tufted bouclé
  curveBed: assets.threadBed2,           // curved bouclé wingback
  oversizedBed: assets.lightJapandiDining, // oversized rounded bouclé bed
  channelBed2: assets.threadCurvedSofa,  // channel-tufted bed (mislabel fix)
  slatHeadboard: assets.threadFlow,      // wood-slat arched headboard (TIMBER!)
  moodyTimberBed: assets.threadNordic,   // dark walnut moody bedroom
  floatingPlatform: assets.threadStudio2, // floating wood platform w/ orbs
  // Sofas / Lounge
  modularSofaPeach: assets.threadStudioApt,
  curveBoucleSofa: assets.threadShopInspo,
  bouCurvedDaybed: assets.threadBestHome,
  bouCurvedDaybed2: assets.timberLimewash,
  linenDaybed: assets.threadStyled,
  waveChaise: assets.threadWave,
  // Tables / Consoles
  walnutWaveConsole: assets.timberMaster,
  walnutWaveConsole2: assets.threadCurvedBed,
  floatingBedClean: assets.timberEntryway,
  // Lighting
  cloudPendants: assets.japandiDining,
  stackedSlatePendants: assets.timberFloatingPlans,
  // Unsplash extras for variety
  uWalnutBed: '/media/u-walnut-bed.jpeg',
  uBoucleLiving: '/media/u-boucle-living.jpeg',
  uPendantLight: '/media/u-pendant-light.jpeg',
  uWalnutConsole: '/media/u-walnut-console.jpeg',
  uCoffeeTable: '/media/u-coffee-table.jpeg',
  uArchHeadboard: '/media/u-arch-headboard.jpeg',
  uChannelBed: '/media/u-channel-bed.jpeg',
  uCurveSofa: '/media/u-curve-sofa.jpeg',
};

const M = A2;

// ============ HERO ============
export const hero = {
  video: A.videoHero,
  poster: A.branded,
  fallback: A.branded,
};

// ============ COLLECTIONS (top-level) ============
export const collections = [
  {
    id: 'thread',
    name: 'Thread Collection',
    subtitle: 'Bouclé · Linen · Upholstered',
    description:
      'Everything wrapped, tufted, and fabric-led. Hand-upholstered in heavyweight bouclé and linen — the softness you can feel before you touch it.',
    image: A.cloudBoucleBed,
    hero: A.cloudBoucleBed,
    accent: '#E8DBC4',
    dark: false,
  },
  {
    id: 'timber',
    name: 'Timber Collection',
    subtitle: 'Solid timber — built to outlive trends',
    description:
      'Walnut, oak, and Tasmanian hardwood. Hand-joined, hand-finished, built to be passed on — not replaced.',
    image: A.timberHero,
    hero: A.timberHero,
    accent: '#3A2417',
    dark: true,
  },
];

// ============ PRODUCTS ============
// Two real Shopify products live via Storefront API.
// Below are MOCK preorder products — `preorder: true` flips UX to "reserve" instead of cart add.
//
// STRICT PRODUCT ISOLATION RULES
// 1. Each `media.primary_image` MUST be unique across all products (no sharing).
// 2. `media.hover_image` MUST belong to the same product_id (it's optional; if absent, no hover swap).
// 3. No product image may be reused in homepage/collection roles and vice-versa.
// `validateProductIsolation()` runs at module load and warns if any rule is violated.
export const preorderProducts = []; // Purged — all products now live in DB (admin /admin → Products)

// ============ LOCAL FALLBACK FOR SHOPIFY PRODUCTS ============
// These are used automatically when SHOPIFY_ENABLED is false OR the Storefront
// API returns nothing. They mirror the two real Shopify products so that the
// storefront keeps working with 100% local media after the user migrates off Shopify.
// IMPORTANT: every asset here is self-hosted under /media (no Shopify CDN URLs).
export const localFallbackProducts = []; // Purged — all products now live in DB

// ============ PRODUCT MEDIA NORMALIZER ============
// Single source of truth for reading a product's media regardless of shape.
// - Local mock products use the strict `media` schema.
// - Shopify products carry `images` from Storefront API (we treat the first as primary).
// Returns a normalized { primary, hover, gallery, productId } object.
export const getProductMedia = (product) => {
  if (!product) return { primary: '/placeholder.png', hover: null, gallery: [], productId: null };

  // Mock products: strict isolated schema
  if (product.media && product.media.primary_image) {
    return {
      primary: product.media.primary_image,
      hover: product.media.hover_image || null,
      gallery: product.media.gallery_images?.length ? product.media.gallery_images : [product.media.primary_image],
      productId: product.id,
    };
  }

  // Shopify products: images array — only this product's own assets, so isolation is preserved by design.
  const imgs = Array.isArray(product.images) ? product.images.filter(Boolean) : [];
  return {
    primary: imgs[0] || '/placeholder.png',
    hover: imgs[1] || null,
    gallery: imgs.length ? imgs : ['/placeholder.png'],
    productId: product.id,
  };
};

// ============ PRODUCT ISOLATION VALIDATION ============
// Runs at module load. Warns (does not throw) if isolation rules are broken so
// the dev gets clear feedback without crashing the storefront.
export const validateProductIsolation = (products = preorderProducts) => {
  const errors = [];
  const ownership = new Map(); // imagePath -> product_id

  products.forEach((p) => {
    const m = p.media;
    if (!m || !m.primary_image) {
      errors.push(`[isolation] ${p.id}: missing media.primary_image`);
      return;
    }
    const assets = [m.primary_image, ...(m.hover_image ? [m.hover_image] : []), ...(m.gallery_images || [])];
    const uniq = [...new Set(assets)];
    uniq.forEach((src) => {
      if (ownership.has(src) && ownership.get(src) !== p.id) {
        errors.push(`[isolation] image "${src}" owned by "${ownership.get(src)}" cannot be reused by "${p.id}"`);
      } else {
        ownership.set(src, p.id);
      }
    });
    if (m.hover_image && !uniq.includes(m.hover_image)) {
      errors.push(`[isolation] ${p.id}: hover_image must belong to this product`);
    }
    // Material consistency: boucle-tagged products must reference boucle asset names
    if (p.material === 'boucle') {
      const ok = uniq.every((src) => /(boucle|chaise|sofa|bed)/i.test(src));
      if (!ok) errors.push(`[material] ${p.id}: boucle product must reference boucle/sofa/chaise/bed assets`);
    }
  });

  if (errors.length && typeof console !== 'undefined' && process.env.NODE_ENV !== 'production') {
    console.warn('Product isolation violations:\n' + errors.join('\n'));
  }
  return { ok: errors.length === 0, errors };
};

if (typeof window !== 'undefined') {
  // Run once on first import in the browser
  validateProductIsolation();
}

// ============ COLLECTIONS CATALOG (DEPRECATED) ============
// Kept as an empty placeholder so any straggling import does not crash.
// /collections + /collections/:slug now read live taxonomy from
// /api/collections-taxonomy via useTaxonomy().
export const collectionsCatalog = [];

// ============ BUILD-THE-ROOM BUNDLES (DEPRECATED) ============
// Replaced by the admin-managed `bundles` collection. Kept as an empty
// object so any straggling import resolves harmlessly.
export const bundleSuggestions = {};

// ============ FEATURED on homepage ============
export const features = [
  { title: 'Free Australia-wide delivery', description: 'On every order. No hidden costs.', image: A.lifeWarm },
  { title: '30-day returns', description: 'Any problems, any reason — we take it back within 30 days. No fees, no fuss.', image: A.threadBed4 },
  { title: 'Built to outlive the trend', description: '2 years of warranty on every frame. We are not worried — you should not be either.', image: A.timberHero },
];

// ============ LIFESTYLE GALLERY (curated, no cropped-product shots) ============
// Rule: every tile must show its subject in full. If layout would crop key objects,
// remove the tile rather than ship a partial frame.
export const lifestyleGallery = [
  { src: A.lifeSofa, span: 'tall' },
  { src: A.lifeOlive, span: 'wide' },
  { src: A.lifeEvening, span: 'square' },
];

// ============ MATERIALS (close-up shots) ============
export const materials = [
  { n: '01', name: 'Australian hardwood', desc: 'Solid Tasmanian oak and American walnut, kiln-dried and finished by hand. No MDF. No veneer pretending to be solid.', image: '/media/m-wood-grain.jpeg' },
  { n: '02', name: 'Heavyweight bouclé', desc: 'A premium 380gsm bouclé woven for upholstery — dense, textured, engineered to hide everyday life.', image: '/media/m-boucle-fabric.jpeg' },
  { n: '03', name: 'Joinery, not screws', desc: 'Mortise-and-tenon construction reinforced with steel. Tool-free assembly in under 30 minutes.', image: '/media/m-joinery.jpeg' },
];

// stats: removed — Home hero now uses <HeroStats /> with live data from /api/reviews/summary.

export const testimonials = [
  { quote: 'Genuinely the nicest piece of furniture we own. Looks like a hotel suite, sleeps like a holiday.', author: 'Aria K.', location: 'Sydney, NSW', rating: 5, product: 'Cloud Bouclé Bed' },
  { quote: 'I expected good. I did not expect this good. The bouclé is thick, the timber is honest, and assembly took 22 minutes.', author: 'Marcus R.', location: 'Melbourne, VIC', rating: 5, product: 'The Ridge Bouclé Bed' },
  { quote: 'Three friends have asked where it is from in the first month. The headboard is a moment.', author: 'Imogen L.', location: 'Brisbane, QLD', rating: 5, product: 'Cloud Bouclé Bed' },
  { quote: 'We replaced a $3.5k brand-name bed with this. Feels heavier, looks better, costs half.', author: 'Daniel & Sara P.', location: 'Perth, WA', rating: 5, product: 'The Ridge' },
  { quote: 'Bouclé soft enough to lean against and read for hours. Frame is rock solid. Easy yes.', author: 'Tahlia M.', location: 'Adelaide, SA', rating: 5, product: 'Cloud Bouclé Bed' },
];

export const journal = [
  { id: 'why-boucle', title: 'Why bouclé belongs in the bedroom', excerpt: 'Beyond the trend — what makes a bouclé bed sit so quietly in a room.', image: A.threadBed1, date: 'Nov 2025', category: 'Style', read: '4 min' },
  { id: 'flat-pack-no-thanks', title: 'Flat-pack, no thanks: why we build slow', excerpt: 'A short read on joinery, materials, and the maths behind a frame that outlives the trend.', image: A.timberHero, date: 'Oct 2025', category: 'Craft', read: '6 min' },
  { id: 'styling-the-boucle-bed', title: 'Styling the bouclé bed: three rooms, three moods', excerpt: 'From warm minimalist to layered and lived-in.', image: A.lifeEvening, date: 'Sep 2025', category: 'Style', read: '5 min' },
];

export const faqs = [
  { q: 'How long does delivery take?', a: 'In-stock items ship within 2–3 business days and arrive in 5–10 business days, free anywhere in Australia. Preorder pieces are made on request — please allow 6–10 weeks.' },
  { q: 'What does "Preorder" mean?', a: 'Preorder products are made on request in Melbourne. Reserve yours and we will confirm a build slot and ETA via email within 24 hours.' },
  { q: 'Do I need a box spring?', a: 'No. Every frame ships with a slatted base engineered for use directly with your mattress.' },
  { q: 'What sizes are available?', a: 'Each frame ships in the sizes listed on the product page — sized to standard Australian mattresses. Check the product page for the exact sizes that frame is available in.' },
  { q: 'Is assembly difficult?', a: 'Tool-free, four-piece assembly. Two people, around 25–30 minutes. We include clear instructions and a small toolkit.' },
  { q: 'How do 30-day returns work?', a: 'If anything is not right within 30 days of delivery, we arrange free pickup and a full refund — no questions, no restocking fees. (Returns apply to in-stock items; preorders are made to your spec and final.)' },
  { q: 'What does the warranty cover?', a: 'Two years on frame, joinery, and upholstery against manufacturing defects. Real, in-Australia support.' },
];

export const pressLogos = ['Vogue Living', 'Belle', 'Inside Out', 'Broadsheet', 'Real Living', 'Est Magazine'];

export const instagramTiles = [A.threadBed1, A.threadBed3, A.lifeEvening, A.lifeOlive, A.cloudBoucleBed, A.threadCurvedSofa];

// Aliases (backwards compat)
export const heroImage = A.branded;
export const lifestyleImage = A.lifeEvening;

export const palette = {
  cream: '#F5EDDD', parchment: '#EDE3CF', sand: '#E3D5B8', taupe: '#C9B596',
  caramel: '#A07A4F', walnut: '#5B3A24', espresso: '#33200F', ink: '#1E140B', bone: '#FBF6EC',
};
